import { ApplicationGameConfig } from '@cgs/shared';
import { INumberFormatter } from './i-number-formatter';

const ONE_K = 1000;
const TEN_K = 10000;
const ONE_M = 1000000;
const TEN_M = 10000000;
const HUNDRED_M = 100000000;
const ONE_B = 1000000000;
const TEN_B = 10000000000;
const HUNDRED_B = 100000000000;
const ONE_T = 1000000000000;
const ONE_Q = 1000000000000000;

const DIVIDERS: [number, string][] = [
  [ONE_K, 'K'],
  [ONE_M, 'M'],
  [ONE_B, 'B'],
  [ONE_T, 't'],
  [ONE_Q, 'q'],
];

export class NumberFormatter implements INumberFormatter {
  public format(value: number, numDecimals: number = 2): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: numDecimals,
      maximumFractionDigits: numDecimals,
    }).format(value);
  }

  public formatMoney(value: number, numDecimals?: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: ApplicationGameConfig.currency,
      minimumFractionDigits: numDecimals,
      maximumFractionDigits: numDecimals,
    }).format(value);
  }

  public formatMoneyShort(money: number, numDigits = 0): string {
    let result = this.formatMoney(money);
    if (this.getDigitsCount(result) <= numDigits) {
      return result;
    }

    for (const divider of DIVIDERS) {
      result = this.formatMoney(this.divideWithOneDecimal(money, divider[0]), 1);
      if (this.getDigitsCount(result) <= numDigits) {
        return result + divider[1];
      }

      result = this.formatIntegerRoundDown(money / divider[0]);
      if (this.getDigitsCount(result) <= numDigits) {
        return result + divider[1];
      }
    }

    return result + 'B';
  }

  public formatValueShortDownBonus(
    jackpotValue: number,
    numDigitsRaw = 8,
    roundUp = false,
    trailingZero = false
  ): string {
    if (Math.trunc(jackpotValue) < 1000) {
      return this.format(Math.trunc(jackpotValue), 0);
    }

    let result = trailingZero
      ? this.format(Math.trunc(jackpotValue), 1)
      : this.format(Math.trunc(jackpotValue), 0);

    const absqLength = 1;
    const dotLength = trailingZero ? 1 : 0;
    const numDigits = numDigitsRaw - absqLength - dotLength;

    if (this.getDigitsCount(result) <= numDigits) {
      return result;
    }

    for (const divider of DIVIDERS) {
      result = this.format(
        this.divideWithOneDecimalRoundDown(jackpotValue, divider[0], roundUp),
        1
      );
      if (this.getDigitsCount(result) <= numDigits) {
        return result + divider[1];
      }

      result = this.formatIntegerRoundDown(jackpotValue / divider[0], roundUp);
      if (this.getDigitsCount(result) <= numDigits) {
        return result + divider[1];
      }
    }

    return result + 'B';
  }

  public formatJackpotLong(val: number, roundUp = false): string {
    const longVal = val;

    if (longVal < TEN_M) {
      return this.formatInteger(longVal, roundUp);
    }
    if (longVal >= TEN_M && longVal < HUNDRED_M) {
      return this.formatDecimal(this.divideWithOneDecimal(longVal, ONE_K, roundUp)) + 'K';
    }
    if (longVal >= HUNDRED_M && longVal < ONE_B) {
      return this.formatInteger(longVal / ONE_K, roundUp) + 'K';
    }
    if (longVal >= ONE_B && longVal < TEN_B) {
      return this.formatDecimal(this.divideWithOneDecimal(longVal, ONE_M, roundUp)) + 'M';
    }
    if (longVal >= TEN_B && longVal < HUNDRED_B) {
      return this.formatInteger(longVal / ONE_M, roundUp) + 'M';
    }
    if (longVal >= HUNDRED_B && longVal < ONE_T) {
      return this.formatDecimal(this.divideWithOneDecimal(longVal, ONE_B, roundUp)) + 'B';
    }
    return this.formatInteger(longVal / ONE_B, roundUp) + 'B';
  }

  public formatLongCoins(val: number, withSpace = false): string {
    const longVal = val;
    if (val < TEN_K) {
      return this.formatInteger(longVal);
    }
    if (val < ONE_M) {
      return (
        this.formatDecimal(this.divideWithOneDecimal(longVal, ONE_K)) + (withSpace ? ' K' : 'K')
      );
    }
    if (val < ONE_B) {
      return (
        this.formatDecimal(this.divideWithOneDecimal(longVal, ONE_M)) + (withSpace ? ' M' : 'M')
      );
    }
    if (val < ONE_T) {
      return (
        this.formatDecimal(this.divideWithOneDecimal(longVal, ONE_B)) + (withSpace ? ' B' : 'B')
      );
    }
    return this.formatDecimal(this.divideWithOneDecimal(longVal, ONE_T)) + (withSpace ? ' t' : 't');
  }

  public formatLongCoins1K(val: number, withSpace = false, numCount = 0): string {
    const longVal = val;
    if (val < ONE_K) {
      return this.formatInteger(longVal);
    }
    if (val < ONE_M) {
      return (
        this.formatDecimalDown(this.divideWithOneDecimal(longVal, ONE_K), numCount) +
        (withSpace ? ' K' : 'K')
      );
    }
    if (val < ONE_B) {
      return (
        this.formatDecimalDown(this.divideWithOneDecimal(longVal, ONE_M), numCount) +
        (withSpace ? ' M' : 'M')
      );
    }
    if (val < ONE_T) {
      return (
        this.formatDecimalDown(this.divideWithOneDecimal(longVal, ONE_B), numCount) +
        (withSpace ? ' B' : 'B')
      );
    }

    return (
      this.formatDecimalDown(this.divideWithOneDecimal(longVal, ONE_T), numCount) +
      (withSpace ? ' t' : 't')
    );
  }

  public formatWinLong(val: number, targetLength: number): string {
    if (targetLength < 4) {
      throw new Error('targetLength must be greater or equal than 4');
    }

    const valueLength = val > 0 ? Math.trunc(this.log10(Math.abs(val + 0.000001)) + 1) : 0;
    const dotLength = 0;
    const abbrLength = 1;

    if (valueLength <= targetLength) {
      return this.format(val, 2);
    }

    const targetValueLength = targetLength - (dotLength + abbrLength);
    const lengthDelta = Math.min(valueLength - targetValueLength, 24);
    let closestAbbrDelta = Math.floor(lengthDelta / 3) * 3;
    if (lengthDelta % 3 !== 0) {
      closestAbbrDelta += 3;
    }
    const decimals = closestAbbrDelta - lengthDelta;
    const mantissa = Math.trunc(val / Math.pow(10, lengthDelta));
    const reducedValue = mantissa / Math.pow(10, decimals);

    let abbreviation: string;
    switch (closestAbbrDelta) {
      case 3:
        abbreviation = 'K';
        break;
      case 6:
        abbreviation = 'M';
        break;
      case 9:
        abbreviation = 'B';
        break;
      case 12:
        abbreviation = 't';
        break;
      case 15:
        abbreviation = 'q';
        break;
      case 18:
        abbreviation = 'Q';
        break;
      case 21:
        abbreviation = 's';
        break;
      case 24:
        abbreviation = 'S';
        break;
      default:
        abbreviation = 'B';
        break;
    }

    return this.format(reducedValue, 2) + abbreviation;
  }

  private log10(value: number): number {
    return Math.log(value) / Math.log(10);
  }

  private divideWithOneDecimal(longVal: number, divisor: number, roundUp = false): number {
    let integerDivision = (longVal / divisor) * 10;
    if (roundUp && integerDivision < (longVal / divisor) * 10.0) {
      integerDivision += 1;
    }
    return integerDivision / 10.0;
  }

  private divideWithOneDecimalRoundDown(longVal: number, divisor: number, roundUp = false): number {
    let integerDivision = Math.floor((longVal / divisor) * 10);
    if (roundUp && integerDivision < (longVal / divisor) * 10.0) {
      integerDivision += 1;
    }
    return integerDivision / 10.0;
  }

  private formatDecimal(val: number): string {
    return this._formatCurrency(val, false, 1);
  }

  private formatInteger(val: number, roundUp = false): string {
    if (!roundUp) {
      return this._formatCurrency(val, false, 0);
    }
    return this._formatCurrency(Math.ceil(val), false, 0);
  }

  private formatDecimalDown(val: number, numCount = 0): string {
    val = Math.floor(val * 10) / 10;
    let result = this._formatCurrency(val, false, 1);
    if (numCount > 0) {
      let parts = val.toString().split(',');
      if (val.toString().includes('.')) {
        parts = val.toString().split('.');
      }
      if (parts[0].length >= numCount) {
        result = this._formatCurrency(Math.floor(val), false, 1);
      }
    }
    return result;
  }

  private formatIntegerRoundDown(val: number, roundUp = false): string {
    if (!roundUp) {
      return this._formatCurrency(Math.floor(val), false, 0);
    }
    return this._formatCurrency(Math.ceil(val), false, 0);
  }

  private _formatCurrency(value: number, padRight: boolean, precision = 2): string {
    let sb = '';
    const prec = Math.pow(10, precision);
    const rounded = Math.round(value * prec);
    const quotient = Math.trunc(rounded / prec);
    const groups: number[] = [];
    let tempQuotient = quotient;
    while (tempQuotient >= 1000) {
      const rem = tempQuotient % 1000;
      groups.unshift(rem);
      tempQuotient = Math.trunc(tempQuotient / 1000);
    }

    sb += quotient;
    for (const val of groups) {
      sb += ',';
      sb += val.toString().padStart(3, '0');
    }
    const remained = rounded % prec;
    if (remained !== 0) {
      sb += '.';
      let fraction = remained.toString().padStart(precision, '0');
      if (!padRight) {
        while (fraction.endsWith('0')) {
          fraction = fraction.substring(0, fraction.length - 1);
        }
      }
      sb += fraction;
    }
    return sb.toString();
  }

  private getDigitsCount(number: string): number {
    return number.replace(/\D/g, '').length;
  }
}
